/**
 * A tiny SimulationCraft-style action priority list (PLAN §7).
 * Entries are tried top to bottom; the first one whose conditions hold and whose
 * ability the sim says is castable wins. `rotationAccuracy` occasionally picks the
 * second valid entry instead, to model sloppy play.
 */
import type { AbilityDef, AbilityId, AuraId, Rng, ResourceId, UnitId, Vec2 } from '@keystone/sim';
import { shapeContains } from './geometry';
import { dist } from './geometry';
import { saveKickFor } from './awareness/kick';
import { abilityLocked, auraRemaining, charges, enemiesNear, findAura, hasAura, hpPct, inAbilityRange, resource, resourcePct, type Perception, type RUnit } from './perception';

export type AplCond =
  | { type: 'resource'; resource: ResourceId; atLeast?: number; below?: number; pct?: boolean }
  | { type: 'selfHpBelow'; pct: number }
  | { type: 'targetHpBelow'; pct: number }
  | { type: 'targetHpAbove'; pct: number }
  /**
   * +specs: the target has at least `selfMaxHp` × my maximum health left, in absolute terms: a fight
   * that will last (a boss, a dungeon pack, a dummy), not a field mob about to drop.
   */
  | { type: 'targetHealthAbove'; selfMaxHp: number }
  /** Engaged enemies within `radius` of the target (or self when the target is missing). */
  | { type: 'targets'; atLeast: number; radius?: number }
  | { type: 'selfAura'; auraId: AuraId; minStacks?: number }
  | { type: 'selfLacksAura'; auraId: AuraId }
  /** Target has my aura with more than `minRemainingMs` left (and at least `minStacks`). */
  | { type: 'targetAura'; auraId: AuraId; minRemainingMs?: number; minStacks?: number }
  /** Target lacks my aura or it has under `refreshBelowMs` left (pandemic window). */
  | { type: 'targetNeedsAura'; auraId: AuraId; refreshBelowMs?: number }
  | { type: 'charges'; ability: AbilityId; atLeast: number }
  /** A zone I own with this defId is active and I stand in it. */
  | { type: 'inOwnZone'; zoneId: string }
  | { type: 'inCombat' }
  /** The bot is moving this think (only instants can go out). */
  | { type: 'moving' }
  /**
   * F13: the ability is usable within `ms` (0 when a charge is ready). With `nextCharge`, the time
   * until the next charge comes back instead (0 when already at max charges): "about to cap".
   */
  | { type: 'cooldownBelow'; ability: AbilityId; ms: number; nextCharge?: boolean }
  /** F13: I have this aura and it runs out within `ms` (permanent auras never do). */
  | { type: 'selfAuraRemainingBelow'; auraId: AuraId; ms: number }
  /** F13: at least `atLeast` living party members (me included) below `below` HP, within `radius` of me. */
  | { type: 'injuredAllies'; below: number; atLeast: number; radius?: number }
  /**
   * Kick discipline: my target is mid-way through an interruptible filler cast that disciplined
   * kickers let go (awareness/kick.ts saveKickFor). Gates rotational presses that interrupt as a
   * side effect (Warden Aegis Throw) so they are not spent on filler. Always false without
   * `kickDiscipline` in the context (novices throw into anything).
   */
  | { type: 'targetFillerCast' }
  /** +basekits: centre distance to the current target is above / below `yd` (Headlong engages from range). */
  | { type: 'targetDistAbove'; yd: number }
  | { type: 'targetDistBelow'; yd: number }
  /** +basekits: engaged enemies within `radius` of me (Hoarbind, Backflip). */
  | { type: 'enemiesNearSelf'; atLeast: number; radius: number }
  /** +basekits: some living party member within `radius` (default 40) lacks this aura (Hearthward). */
  | { type: 'partyLacksAura'; auraId: AuraId; radius?: number }
  /** +basekits: my target is casting something interruptible. */
  | { type: 'targetCasting' }
  /** +basekits: living enemies (engaged or not) within `radius` of my target, the target included (opening on a pack). */
  | { type: 'packNearTarget'; atLeast: number; radius: number }
  /** +acolyte-specs: at least `atLeast` living party members (me included) carry my `auraId` (Absolution marks). */
  | { type: 'markedAllies'; auraId: AuraId; atLeast: number }
  /** +acolyte-specs: at least `atLeast` living party members (me included): a group, not solo. */
  | { type: 'partySize'; atLeast: number }
  /** +acolyte-specs: the party's tank lacks my `auraId` or it runs out within `refreshBelowMs`. */
  | { type: 'tankNeedsAura'; auraId: AuraId; refreshBelowMs?: number }
  | { type: 'not'; cond: AplCond }
  | { type: 'any'; of: AplCond[] }
  /** F13: AND group (for use inside `any`). */
  | { type: 'all'; of: AplCond[] };

/** Where the cast goes: current enemy target, self, ground under target/self, or an explicit ally. */
export type AplTarget =
  | 'enemy'
  | 'self'
  | 'groundTarget'
  | 'groundSelf'
  | 'none'
  /** +acolyte-specs: the party's tank (alive, else skipped). */
  | 'tank'
  /** +acolyte-specs: the most injured living party member without my `AplEntry.auraId` (wards that mark). */
  | 'unmarkedAlly'
  /** +acolyte-specs: an engaged enemy without my `AplEntry.auraId` (multi-dotting: the nearest to my target). */
  | 'enemyLacking';

export interface AplEntry {
  ability: AbilityId;
  when?: AplCond[];
  target?: AplTarget;
  /** Fine-play line (aura expiry, cap avoidance): skipped by bots without `aplFinesse` (skill dial). */
  finesse?: boolean;
  /** +acolyte-specs: the aura 'unmarkedAlly' / 'enemyLacking' look for (mine). */
  auraId?: AuraId;
}

export interface AplChoice {
  ability: AbilityDef;
  targetId: UnitId | null;
  point: Vec2 | null;
}

export interface AplContext {
  p: Perception;
  target: RUnit | undefined;
  /** Skip `finesse` entries (novice play). */
  noFinesse?: boolean;
  /** The bot is moving this think: cast-time spells that can't be cast on the move are skipped. */
  moving?: boolean;
  /** Skill dial: the bot saves interrupts for priority casts (enables `targetFillerCast`). */
  kickDiscipline?: boolean;
  /** +grid: Auto's wits: own-health thresholds fire this much earlier (BotSkillProfile.hpBias). */
  hpBias?: number;
}

export function evalCond(c: AplCond, ctx: AplContext): boolean {
  const { p, target } = ctx;
  const self = p.self;
  switch (c.type) {
    case 'resource': {
      const v = c.pct ? resourcePct(self, c.resource) : resource(self, c.resource);
      return (c.atLeast === undefined || v >= c.atLeast) && (c.below === undefined || v < c.below);
    }
    case 'selfHpBelow':
      return hpPct(self) < c.pct + (ctx.hpBias ?? 0); // +grid
    case 'targetHpBelow':
      return !!target && hpPct(target) < c.pct;
    case 'targetHpAbove':
      return !!target && hpPct(target) > c.pct;
    case 'targetHealthAbove':
      return !!target && target.hp >= c.selfMaxHp * self.maxHp;
    case 'targets':
      return enemiesNear(p, (target ?? self).pos, c.radius ?? 8) >= c.atLeast;
    case 'selfAura': {
      const a = self.auras.find((x) => x.auraId === c.auraId);
      return !!a && a.stacks >= (c.minStacks ?? 1);
    }
    case 'selfLacksAura':
      return !hasAura(self, c.auraId);
    case 'targetAura': {
      if (!target || auraRemaining(target, c.auraId, p.now, self.id) <= (c.minRemainingMs ?? 0)) return false;
      if (c.minStacks === undefined) return true;
      return (findAura(target, c.auraId, self.id)?.stacks ?? 0) >= c.minStacks;
    }
    case 'cooldownBelow': {
      const cd = self.cooldowns[c.ability];
      if (c.nextCharge) return !cd || cd.rechargeAt === null || cd.charges >= cd.maxCharges || cd.rechargeAt - p.now < c.ms;
      return p.view.queries.cooldownRemaining(self.id, c.ability) < c.ms;
    }
    case 'selfAuraRemainingBelow': {
      if (!hasAura(self, c.auraId)) return false;
      return auraRemaining(self, c.auraId, p.now) < c.ms;
    }
    case 'injuredAllies': {
      let n = 0;
      for (const u of p.party) {
        if (!u.alive || hpPct(u) >= c.below) continue;
        if (c.radius !== undefined && dist(u.pos, self.pos) > c.radius) continue;
        n++;
      }
      return n >= c.atLeast;
    }
    case 'targetNeedsAura':
      return !!target && auraRemaining(target, c.auraId, p.now, self.id) <= (c.refreshBelowMs ?? 0);
    case 'charges': {
      const ab = p.view.content.ability(c.ability);
      return !!ab && charges(self, ab) >= c.atLeast;
    }
    case 'inOwnZone':
      return p.world.zones.some((z) => z.defId === c.zoneId && z.sourceId === self.id && shapeContains(z.area, self.pos));
    case 'inCombat':
      return p.engaged.length > 0;
    case 'moving':
      return !!ctx.moving;
    case 'targetDistAbove':
      return !!target && dist(target.pos, self.pos) > c.yd;
    case 'targetDistBelow':
      return !!target && dist(target.pos, self.pos) < c.yd;
    case 'enemiesNearSelf':
      return enemiesNear(p, self.pos, c.radius) >= c.atLeast;
    case 'partyLacksAura':
      return p.party.some((u) => u.alive && dist(u.pos, self.pos) <= (c.radius ?? 40) && !hasAura(u, c.auraId));
    case 'packNearTarget':
      return !!target && p.enemies.filter((e) => e.alive && dist(e.pos, target.pos) <= c.radius + e.radius).length >= c.atLeast;
    case 'targetCasting':
      return !!target?.cast?.interruptible && target.cast.endsAt > p.now;
    case 'targetFillerCast':
      return !!ctx.kickDiscipline && !!target?.cast?.interruptible && target.cast.endsAt > p.now && saveKickFor(p, target);
    case 'markedAllies': // +acolyte-specs
      return p.party.filter((u) => u.alive && hasMine(u, c.auraId, self.id)).length >= c.atLeast;
    case 'partySize':
      return p.party.length >= c.atLeast;
    case 'tankNeedsAura': {
      const tank = p.tank;
      return !!tank?.alive && auraRemaining(tank, c.auraId, p.now, self.id) <= (c.refreshBelowMs ?? 0);
    }
    case 'not':
      return !evalCond(c.cond, ctx);
    case 'any':
      return c.of.some((x) => evalCond(x, ctx));
    case 'all':
      return c.of.every((x) => evalCond(x, ctx));
  }
}

function resolveTarget(entry: AplEntry, ability: AbilityDef, ctx: AplContext): { targetId: UnitId | null; point: Vec2 | null } | null {
  const { p, target } = ctx;
  const kind: AplTarget =
    entry.target ??
    (ability.targeting === 'enemy' ? 'enemy' : ability.targeting === 'ground' ? 'groundTarget' : ability.targeting === 'ally' ? 'self' : 'none');
  switch (kind) {
    case 'enemy':
      return target ? { targetId: target.id, point: null } : null;
    case 'self':
      return { targetId: p.self.id, point: null };
    case 'groundTarget':
      return target ? { targetId: null, point: { ...target.pos } } : null;
    case 'groundSelf':
      return { targetId: null, point: { ...p.self.pos } };
    case 'none':
      return { targetId: null, point: null };
    // +acolyte-specs: ally / multi-dot targets.
    case 'tank':
      return p.tank?.alive ? { targetId: p.tank.id, point: null } : null;
    case 'unmarkedAlly': {
      if (!entry.auraId) return null;
      // In a group the healer never marks itself (it is not the one taking damage); alone it does.
      const alone = p.party.length <= 1;
      const pick = p.party.filter((u) => u.alive && (alone || u.id !== p.self.id) && !hasMine(u, entry.auraId!, p.self.id) && dist(u.pos, p.self.pos) <= 40).sort((a, b) => hpPct(a) - hpPct(b) || (a.id < b.id ? -1 : 1))[0];
      return pick ? { targetId: pick.id, point: null } : null;
    }
    case 'enemyLacking': {
      if (!entry.auraId) return null;
      const from = target?.pos ?? p.self.pos;
      const pick = p.engaged
        .filter((e) => e.alive && !hasMine(e, entry.auraId!, p.self.id) && inAbilityRange(p.self, e, ability))
        .sort((a, b) => dist(a.pos, from) - dist(b.pos, from) || (a.id < b.id ? -1 : 1))[0];
      return pick ? { targetId: pick.id, point: null } : null;
    }
  }
}

/** +acolyte-specs: `u` carries `auraId` cast by `casterId`. */
function hasMine(u: RUnit, auraId: AuraId, casterId: UnitId): boolean {
  return u.auras.some((a) => a.auraId === auraId && a.casterId === casterId);
}

/** Instant (or move-castable) after the sim resolves overrides (Lava Surge, Infusion: castTimePct −1). */
export function castableWhileMoving(p: Perception, base: AbilityDef): boolean {
  const id = p.view.queries.resolveAbility?.(p.self.id, base.id) ?? p.self.abilityOverrides?.[base.id] ?? base.id;
  const ab = id === base.id ? base : (p.view.content.ability(id) ?? base);
  if (!ab.cast || ab.cast.type === 'instant' || ab.canCastWhileMoving) return true;
  if (ab.cast.type === 'channel') return false;
  return p.self.auras.some((a) =>
    p.view.content.aura(a.auraId)?.mods?.some((m) => m.stat === 'castTimePct' && m.value <= -1 && (!m.abilityIds || m.abilityIds.includes(ab.id))),
  );
}

/** All entries that are currently valid, in priority order. */
export function validChoices(apl: readonly AplEntry[], ctx: AplContext, limit = 2): AplChoice[] {
  const out: AplChoice[] = [];
  const seen = new Set<AbilityId>();
  for (const entry of apl) {
    if (seen.has(entry.ability)) continue;
    if (entry.finesse && ctx.noFinesse) continue;
    const ability = ctx.p.view.content.ability(entry.ability);
    if (!ability) continue;
    if (abilityLocked(ctx.p.view, ctx.p.self, ability.id)) continue; // +field
    if (ctx.moving && !castableWhileMoving(ctx.p, ability)) continue;
    if (entry.when && !entry.when.every((c) => evalCond(c, ctx))) continue;
    const t = resolveTarget(entry, ability, ctx);
    if (!t) continue;
    const tu = t.targetId ? ctx.p.world.units[t.targetId] : undefined;
    if (tu && tu.id !== ctx.p.self.id && !inAbilityRange(ctx.p.self, tu, ability)) continue;
    // 'not_facing' is fine: withCast() turns to the target in the same command batch.
    const r = ctx.p.view.queries.canCast(ctx.p.self.id, ability.id, t.targetId, t.point);
    if (!r.ok && r.reason !== 'not_facing') continue; // includes +field 'locked'
    seen.add(entry.ability);
    out.push({ ability, ...t });
    if (out.length >= limit) break;
  }
  return out;
}

/** Pick the top choice, or the runner-up with probability (1 - accuracy). */
export function pickChoice(apl: readonly AplEntry[], ctx: AplContext, accuracy: number, rng: Rng): AplChoice | null {
  const valid = validChoices(apl, ctx, 2);
  if (valid.length === 0) return null;
  if (valid.length > 1 && !rng.chance(accuracy)) return valid[1] ?? null;
  return valid[0] ?? null;
}
